import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
	apiGetAllPlatformsClients,
	apiGetAllProjectClassification,
	apiGetAllProjectNumber,
	apiGetAllProjectScope,
	apiGetAllUsers,
	apiGetAllOperationRoles,
	apiGetAllSkills,
	apiGetAllWorkerNames,
	apiGetAllEvents,
	apiGetAllContractCategory,
	apiGetScheduleView,
	IClientBackEnd,
	IPlatformBackEnd,
	IProjectScopeBackEnd,
	IProjectClassificationBackEnd,
	IUserBackEnd,
	IOperationRoleBackEnd,
	ISkill,
	IWorkerNameBackEnd,
	IDrakeEventBackEnd,
	IContractCategoryBackEnd,
} from '../../../services/apiService';
import axios from 'axios';
import {
	removeDuplicatesByIdUtil,
	removeDuplicatesByPlatformCode,
} from '../../../utils/clientsBackEnd';

interface FiltersProps {
	setScheduleViewData: (data: { workers: any[]; days: string[] }) => void;
	setIsLoading: (val: boolean) => void;
	startDate: string;
	setStartDate: (d: string) => void;
	endDate: string;
	setEndDate: (d: string) => void;
}

const Filters: React.FC<FiltersProps> = ({
	setScheduleViewData,
	setIsLoading,
	startDate,
	setStartDate,
	endDate,
	setEndDate,
}) => {
	const [clients, setClients] = useState<IClientBackEnd[]>([]);
	const [platforms, setPlatforms] = useState<IPlatformBackEnd[]>([]);
	const [filteredPlatforms, setFilteredPlatforms] = useState<
		IPlatformBackEnd[]
	>([]);
	const [projectNumbers, setProjectNumbers] = useState<number[]>([]);
	const [users, setUsers] = useState<IUserBackEnd[]>([]);
	const [scopes, setScopes] = useState<IProjectScopeBackEnd[]>([]);
	const [classes, setClasses] = useState<IProjectClassificationBackEnd[]>([]);
	const [opRoles, setOpRoles] = useState<IOperationRoleBackEnd[]>([]);
	const [skills, setSkills] = useState<ISkill[]>([]);
	const [workers, setWorkers] = useState<IWorkerNameBackEnd[]>([]);
	const [events, setEvents] = useState<IDrakeEventBackEnd[]>([]);
	const [contracts, setContracts] = useState<IContractCategoryBackEnd[]>([]);

	const [selectedClient, setSelectedClient] = useState<number | ''>('');
	const [selectedPlatform, setSelectedPlatform] = useState<number | ''>('');
	const [selectedProjectNumber, setSelectedProjectNumber] = useState<
		number | ''
	>('');
	const [selectedUser, setSelectedUser] = useState<number | ''>('');
	const [selectedScope, setSelectedScope] = useState<number | ''>('');
	const [selectedClass, setSelectedClass] = useState<number | ''>('');
	const [selectedOpRoles, setSelectedOpRoles] = useState<number[]>([]);
	const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>([]);
	const [selectedWorkerIds, setSelectedWorkerIds] = useState<number[]>([]);
	const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
	const [selectedContracts, setSelectedContracts] = useState<number[]>([]);
	const [getUnscheduled, setGetUnscheduled] = useState<boolean>(true);

	useEffect(() => {
		async function load() {
			try {
				const backEndAllPlatformsClients = await apiGetAllPlatformsClients();
				const clients = backEndAllPlatformsClients.map(obj => obj.client);
				const clientsUnique = removeDuplicatesByIdUtil(clients);
				const platformsUnique = removeDuplicatesByPlatformCode(
					backEndAllPlatformsClients
				);
				setClients(clientsUnique);
				setPlatforms(platformsUnique);
				setFilteredPlatforms(platformsUnique);
			} catch (error) {
				toast.error(
					'ERROR: falha ao carregar os dados de clientes e plataformas.'
				);
			}
			try {
				const nums = await apiGetAllProjectNumber();
				setProjectNumbers(nums.map(p => p.project_number));
			} catch {
				toast.error('Erro ao carregar WOs');
			}
			try {
				setUsers(await apiGetAllUsers());
			} catch {
				toast.error('Erro ao carregar usuários');
			}
			try {
				setScopes(await apiGetAllProjectScope());
			} catch {
				toast.error('Erro ao carregar escopos');
			}
			try {
				setClasses(await apiGetAllProjectClassification());
			} catch {
				toast.error('Erro ao carregar classificações');
			}
			try {
				setOpRoles(await apiGetAllOperationRoles());
			} catch {
				toast.error('Erro ao carregar funções');
			}
			try {
				setSkills(await apiGetAllSkills());
			} catch {
				toast.error('Erro ao carregar habilidades');
			}
			try {
				setWorkers(await apiGetAllWorkerNames());
			} catch {
				toast.error('Erro ao carregar trabalhadores');
			}
			try {
				setEvents(await apiGetAllEvents());
			} catch {
				toast.error('Erro ao carregar eventos');
			}
			try {
				setContracts(await apiGetAllContractCategory());
			} catch {
				toast.error('Erro ao carregar categorias');
			}
		}
		load();
	}, []);

	useEffect(() => {
		if (selectedClient) {
			setFilteredPlatforms(
				platforms.filter(p => p.client.id === selectedClient)
			);
		} else {
			setFilteredPlatforms(platforms);
		}
	}, [selectedClient, platforms]);

	const handleFilter = async () => {
		setIsLoading(true);
		try {
			const resp = await apiGetScheduleView(
				startDate,
				endDate,
				selectedClient,
				selectedPlatform,
				selectedProjectNumber,
				selectedScope,
				selectedClass,
				selectedUser,
				selectedOpRoles,
				selectedWorkerIds,
				selectedSkillIds,
				selectedEventIds
					.map(id => events.find(e => e.id === id)?.event_acronym || '')
					.filter(acr => acr !== ''),
				selectedContracts,
				getUnscheduled,
				true
			);
			setScheduleViewData({ workers: resp.workers, days: resp.days });
		} catch (error: any) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				toast.warning('Nenhum dado encontrado.');
				setScheduleViewData({ workers: [], days: [] });
			} else {
				toast.error('Falha ao obter dados.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handleClear = () => {
		setSelectedClient('');
		setSelectedPlatform('');
		setSelectedProjectNumber('');
		setSelectedUser('');
		setSelectedScope('');
		setSelectedClass('');
		setSelectedOpRoles([]);
		setSelectedSkillIds([]);
		setSelectedWorkerIds([]);
		setSelectedEventIds([]);
		setSelectedContracts([]);
		setStartDate('');
		setEndDate('');
	};

	return (
		<Box display="flex" flexDirection="column" gap="20px">
			{/* Primeira linha de filtros */}
			<Box display="flex" flexWrap="wrap" gap="20px">
				<TextField
					label="Data início"
					type="date"
					value={startDate}
					onChange={e => setStartDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					label="Data fim"
					type="date"
					value={endDate}
					onChange={e => setEndDate(e.target.value)}
					InputLabelProps={{ shrink: true }}
				/>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>Cliente</InputLabel>
					<Select
						label="Cliente"
						value={selectedClient}
						onChange={e => {
							setSelectedClient(+e.target.value);
							setSelectedPlatform('');
						}}
					>
						{clients
							.sort((a, b) => a.client_name.localeCompare(b.client_name))
							.map(c => (
								<MenuItem key={c.id} value={c.id}>
									{c.client_name}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>Rigs</InputLabel>
					<Select
						label="Rigs"
						value={selectedPlatform}
						onChange={e => setSelectedPlatform(+e.target.value)}
					>
						{filteredPlatforms
							.sort((a, b) => a.platform_name.localeCompare(b.platform_name))
							.map(p => (
								<MenuItem key={p.id} value={p.id}>
									{p.platform_name}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>WO</InputLabel>
					<Select
						label="WO"
						value={selectedProjectNumber}
						onChange={e => setSelectedProjectNumber(+e.target.value)}
					>
						{projectNumbers
							.sort((a, b) => a - b)
							.map(wo => (
								<MenuItem key={wo} value={wo}>
									{wo}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>Coordenador</InputLabel>
					<Select
						label="Coordenador"
						value={selectedUser}
						onChange={e => setSelectedUser(+e.target.value)}
					>
						{users
							.sort((a, b) => a.username.localeCompare(b.username))
							.filter(user => user.coordinator)
							.map(u => (
								<MenuItem key={u.id} value={u.id}>
									{u.username}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>Escopo</InputLabel>
					<Select
						label="Escopo"
						value={selectedScope}
						onChange={e => setSelectedScope(+e.target.value)}
					>
						{scopes
							.sort((a, b) => a.sow.localeCompare(b.sow))
							.map(s => (
								<MenuItem key={s.id} value={s.id}>
									{s.sow}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 150 }}>
					<InputLabel>Tipo</InputLabel>
					<Select
						label="Tipo"
						value={selectedClass}
						onChange={e => setSelectedClass(+e.target.value)}
					>
						{classes
							.sort((a, b) => a.description.localeCompare(b.description))
							.map(c => (
								<MenuItem key={c.id} value={c.id}>
									{c.description}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Box>

			{/* Segunda linha de múltiplos */}
			<Box display="flex" flexWrap="wrap" gap="20px">
				<FormControl style={{ minWidth: 200 }}>
					<InputLabel>Funções</InputLabel>
					<Select
						label="Funções"
						multiple
						value={selectedOpRoles}
						onChange={e =>
							setSelectedOpRoles(
								typeof e.target.value === 'string'
									? e.target.value.split(',').map(Number)
									: (e.target.value as number[])
							)
						}
						renderValue={selectedIds => {
							// filtra opRoles para pegar só os que estão selecionados
							const descricoesSelecionadas = opRoles
								.filter(role => (selectedIds as number[]).includes(role.id))
								.map(role => role.description);
							return descricoesSelecionadas.join(', ');
						}}
					>
						{opRoles
							.sort((a, b) => a.description.localeCompare(b.description))
							.map(r => (
								<MenuItem key={r.id} value={r.id}>
									{r.description}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 200 }}>
					<InputLabel>Habilidades</InputLabel>
					<Select
						label="Habilidades"
						multiple
						value={selectedSkillIds}
						onChange={e =>
							setSelectedSkillIds(
								typeof e.target.value === 'string'
									? e.target.value.split(',').map(Number)
									: (e.target.value as number[])
							)
						}
						renderValue={selectedIds => {
							const descricoesSelecionadas = skills
								.filter(skill => (selectedIds as number[]).includes(skill.id))
								.map(skill => skill.description);
							return descricoesSelecionadas.join(', ');
						}}
					>
						{skills
							.sort((a, b) => a.description.localeCompare(b.description))
							.map(s => (
								<MenuItem key={s.id} value={s.id}>
									{s.description}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				<FormControl style={{ minWidth: 200 }}>
					<InputLabel>Trabalhadores</InputLabel>
					<Select
						label="Trabalhadores"
						multiple
						value={selectedWorkerIds}
						onChange={e =>
							setSelectedWorkerIds(
								typeof e.target.value === 'string'
									? e.target.value.split(',').map(Number)
									: (e.target.value as number[])
							)
						}
						renderValue={selectedIds => {
							const nomesSelecionados = workers
								.filter(w => (selectedIds as number[]).includes(w.id))
								.map(w => w.worker_name);
							return nomesSelecionados.join(', ');
						}}
					>
						{workers
							.sort((a, b) => a.worker_name.localeCompare(b.worker_name))
							.map(w => (
								<MenuItem key={w.id} value={w.id}>
									{w.worker_name}
								</MenuItem>
							))}
					</Select>
				</FormControl>

				{/* <FormControl style={{ minWidth: 200 }}>
					<InputLabel>Eventos</InputLabel>
					<Select
						label="Eventos"
						multiple
						value={selectedEventIds}
						onChange={e =>
							setSelectedEventIds(
								typeof e.target.value === 'string'
									? e.target.value.split(',').map(Number)
									: (e.target.value as number[])
							)
						}
						renderValue={selectedIds => {
							const acronimosSelecionados = events
								.filter(ev => (selectedIds as number[]).includes(ev.id))
								.map(ev => ev.event_acronym);
							return acronimosSelecionados.join(', ');
						}}
					>
						{events
							.sort((a, b) => a.event_acronym.localeCompare(b.event_acronym))
							.map(ev => (
								<MenuItem key={ev.id} value={ev.id}>
									{ev.event_acronym}
								</MenuItem>
							))}
					</Select>
				</FormControl> */}

				<FormControl style={{ minWidth: 200 }}>
					<InputLabel>Categoria de Contrato</InputLabel>
					<Select
						label="Categoria de Contrato"
						multiple
						value={selectedContracts}
						onChange={e =>
							setSelectedContracts(
								typeof e.target.value === 'string'
									? e.target.value.split(',').map(Number)
									: (e.target.value as number[])
							)
						}
						renderValue={selectedIds => {
							const descricoesSelecionadas = contracts
								.filter(c => (selectedIds as number[]).includes(c.id))
								.map(c => c.description);
							return descricoesSelecionadas.join(', ');
						}}
					>
						{contracts
							.sort((a, b) => a.description.localeCompare(b.description))
							.map(c => (
								<MenuItem key={c.id} value={c.id}>
									{c.description}
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Box>

			{/* Botões de ação */}
			<Box display="flex" gap="20px">
				<Button variant="contained" onClick={handleFilter}>
					Filtrar
				</Button>
				<Button
					variant="contained"
					onClick={handleClear}
					style={{ background: '#e5e5e5', color: '#000' }}
				>
					Limpar
				</Button>
				<FormControlLabel
					control={
						<Checkbox
							checked={getUnscheduled}
							onChange={e => setGetUnscheduled(e.target.checked)}
							name="filterUnscheduled"
							color="primary"
						/>
					}
					label="sem programação"
				/>
			</Box>
		</Box>
	);
};

export default Filters;
